import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { DeleteItemIcon } from '@/assets/static/icons/delete-item';
import Button from '@/components/basicComponents/button';
import { ErrorText } from '@/components/basicComponents/input';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { IContactWithValue } from '@/components/investors-page/investorDetailsPage/tabs/documentationTab/types';
import { useResponsive } from '@/hooks/use-responsive';
import { getUTCDate } from '@/shared/utils/date';
import { getEnumValueByKey } from '@/shared/utils/enums';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { ContactType } from '@trace-8/trace8-schema';
import { useEffect, useMemo, useState } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { ThreeColumnWrapper } from '../styles';
import { IRenderTemplateRow, OpenWizardModalPageArgsType } from '../types';
import { evaluateDependencyItem, shouldRenderEmptyComponent } from '../utils';

export const matchContactDataWithRows = (
  row: IRenderTemplateRow,
  contact: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'][number]
): Record<string, string>[] => {
  const contactData: any = contact?.data || {};

  const contactType = getEnumValueByKey(ContactType, contact?.type);

  const rowsToRender = row?.config?.addContactRows
    ? Array.isArray(row?.config?.addContactRows)
      ? row?.config?.addContactRows
      : row?.config?.addContactRows[contactType]
    : [];

  return rowsToRender.map((row: IRenderTemplateRow) => {
    const { component, dependsOn } = row;
    const hasValidOrDependency: boolean = dependsOn?.or
      ? dependsOn.or.some((item: any) => {
          return evaluateDependencyItem(contactData?.[item.name], item);
        })
      : null;
    const hasValidAndDependency: boolean = dependsOn?.and
      ? dependsOn.and.every((item: any) => {
          return evaluateDependencyItem(contactData?.[item.name], item);
        })
      : null;

    if (shouldRenderEmptyComponent(dependsOn, contactData?.[dependsOn?.name], hasValidOrDependency, hasValidAndDependency)) {
      return { id: guid(), label: '', value: null };
    }

    switch (component) {
      case 'custom':
        return row.fields.map((field) => {
          if (field.name === 'name') return { id: guid(), label: field?.label ?? '', value: contact.name };
          if (field.name === 'email') return { id: guid(), label: field?.label ?? '', value: contact.email };
          switch (field.component) {
            case 'date':
              return contactData[field.name]
                ? { id: guid(), label: field?.label ?? '', value: getUTCDate(contactData[field.name]) }
                : { id: guid(), label: field?.label ?? '', value: null };
            case 'phoneNumber':
              if (!Object.keys(contactData[field.name] || {}).length) return { id: guid(), label: field?.label ?? '', value: null };

              const mapping = field.validation?.mapping;
              const countryCodeField = mapping?.['countryCode'] || 'countryCode';
              const numberField = mapping?.['number'] || 'number';

              const code = contactData[field.name][countryCodeField] as string;
              const number = contactData[field.name][numberField] as string;
              return contactData[field.name]
                ? { id: guid(), label: field?.label ?? '', value: `(+${code}) ${number}` }
                : { id: guid(), label: field?.label ?? '', value: null };
            case 'radio':
              return {
                id: guid(),
                label: field?.label ?? '',
                value: field?.config?.items.find((item: any) => item.value === contactData?.[field.name])?.label ?? ''
              };
            default:
              return { id: guid(), label: field?.label ?? '', value: contactData[field.name] };
          }
        });
      case 'address':
        return [
          {
            id: guid(),
            label: row?.config?.labels?.['streetAddress'] ?? 'Street Address',
            value: contactData?.[row.name]?.[`${row.config?.mapping?.['streetAddress'] ?? 'streetAddress'}`] ?? null
          },
          {
            id: guid(),
            label: row?.config?.labels?.['city'] ?? 'City',
            value: contactData?.[row.name]?.[`${row.config?.mapping?.['city'] ?? 'city'}`] ?? null
          },
          {
            id: guid(),
            label: row?.config?.labels?.['state'] ?? 'State',
            value: contactData?.[row.name]?.[`${row.config?.mapping?.['state'] ?? 'state'}`] ?? null
          },
          {
            id: guid(),
            label: row?.config?.labels?.['zipCode'] ?? 'Zip',
            value: contactData?.[row.name]?.[`${row.config?.mapping?.['zipCode'] ?? 'zipCode'}`] ?? null
          },
          {
            id: guid(),
            label: row?.config?.labels?.['country'] ?? 'Country',
            value: contactData?.[row.name]?.[`${row.config?.mapping?.['country'] ?? 'country'}`] ?? null
          }
        ];
      case 'radio':
        return {
          id: guid(),
          label: row?.label ?? '',
          value: row?.config?.items.find((item: any) => item.value === contactData?.[row.name])?.label ?? ''
        };
      default:
        return { id: guid(), label: row?.label ?? '', value: contactData?.[row.name] || null };
    }
  });
};

export const isContactDataComplete = (
  row: IRenderTemplateRow,
  contact: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'][number]
): boolean => {
  const contactData: Record<string, any> = contact?.data || {};

  const contactType = getEnumValueByKey(ContactType, contact?.type);

  const rowsToRender = row?.config?.addContactRows
    ? Array.isArray(row?.config?.addContactRows)
      ? row?.config?.addContactRows
      : row?.config?.addContactRows[contactType]
    : [];

  return rowsToRender.every((row: IRenderTemplateRow) => {
    const { component, dependsOn } = row;
    const hasValidOrDependency: boolean = dependsOn?.or
      ? dependsOn.or.some((item: any) => {
          return evaluateDependencyItem(contactData?.[item.name], item);
        })
      : null;
    const hasValidAndDependency: boolean = dependsOn?.and
      ? dependsOn.and.every((item: any) => {
          return evaluateDependencyItem(contactData?.[item.name], item);
        })
      : null;

    if (shouldRenderEmptyComponent(dependsOn, contactData?.[dependsOn?.name], hasValidOrDependency, hasValidAndDependency)) {
      return true;
    }

    switch (component) {
      case 'custom':
        return row.fields.every((field) => {
          if (field.name === 'name') return !!contact.name;
          if (field.name === 'email') return !!contact.email;
          if (field.validation?.required && field.component === 'phoneNumber') {
            const phoneNumberValues = Object.values(contactData?.[field.name] || {});
            if (!phoneNumberValues.length) return false;
            return phoneNumberValues.every(Boolean);
          }
          return field.validation?.required ? !!contactData[field.name]?.toString() : true;
        });

      case 'address':
        const addressValues = Object.values(contactData?.[row.name] || {});
        if (!addressValues.length) return false;
        return addressValues.every(Boolean);

      default:
        return row?.validation?.required ? !!contactData?.[row.name]?.toString() : true;
    }
  });
};

interface ContactsComponentProps {
  row: IRenderTemplateRow;
  mode: 'documentationTab' | 'wizard';
  pageName?: string;
  contacts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  ignoreRequired: boolean;
  deleteItem: (type: 'bankAccounts' | 'contacts', id: string) => void;
  openModalPage: ({ type, row, id }: OpenWizardModalPageArgsType) => void;
  entityId: string;
  siblingContactFieldNames?: string[];
}

export const ContactsComponent = ({
  row,
  mode,
  pageName = '',
  contacts,
  ignoreRequired,
  deleteItem,
  openModalPage,
  entityId,
  siblingContactFieldNames = []
}: ContactsComponentProps) => {
  const { isMobile } = useResponsive();
  const { name, dependsOn, validation, config, label: rowLabel } = row;
  const {
    formState: { errors },
    setValue,
    unregister,
    watch
  } = useFormContext();
  const contactsIDs = watch(`${pageName}.${name}`);
  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  // Watch sibling contact fields in the same group
  const siblingFieldValues = siblingContactFieldNames.map((fieldName) => watch(`${pageName}.${fieldName}`));
  const siblingSelectedIds = useMemo(() => {
    return siblingFieldValues.flatMap((value) => {
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    });
  }, [JSON.stringify(siblingFieldValues)]);

  const [contactSelectOptions, setContactSelectOptions] = useState<ISelectValue[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<IContactWithValue[]>([]);
  const [showContactsDropdown, setShowContactsDropdown] = useState(false);

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      unregister(`${pageName}.${name}`);
    }
  }, [dependentValue]);

  useEffect(() => {
    const selectedContacts = contacts?.filter((contact) => contactsIDs?.includes(contact.id));
    const updatedSelectedContacts = selectedContacts?.map((contact) => {
      const matchedContactDataWithRows = matchContactDataWithRows(row, contact);
      if (isContactDataComplete(row, contact) || mode === 'documentationTab') {
        return { ...contact, data: matchedContactDataWithRows.flat(), error: null };
      }
      return { ...contact, data: matchedContactDataWithRows.flat(), error: 'Required fields missing.  Please edit this contact to proceed.' };
    });
    setSelectedContacts(updatedSelectedContacts ?? []);
  }, [row, contacts, contactsIDs]);

  useEffect(() => {
    if (!contacts.length) {
      setContactSelectOptions([{ label: 'Select', value: '' }]);
      return;
    }

    let contactOptions = config?.allowedTypes
      ? contacts.filter((contact) => config?.allowedTypes?.includes(getEnumValueByKey(ContactType, contact.type)))
      : contacts;
    //if the contact config has an isAlreadyAssignedToFamilyRestriction, filter out contacts that are already an investor to an entity in the family
    contactOptions = row?.config?.isAlreadyAssignedToFamilyRestriction
      ? contactOptions.filter((contact) => !contact.isInvestor || (contact.isInvestor && contact.legalEntityId === entityId))
      : contactOptions;

    // Filter out contacts selected in sibling fields (same group)
    contactOptions = contactOptions.filter((contact) => !siblingSelectedIds.includes(contact.id));

    if (config?.selectType === 'multiple') {
      setContactSelectOptions(
        contactOptions.filter((contact) => !contactsIDs?.includes(contact.id)).map((contact) => ({ label: contact.name, value: contact.id }))
      );
      return;
    }

    setContactSelectOptions(
      contactOptions.filter((contact) => contact.id !== contactsIDs).map((contact) => ({ label: contact.name, value: contact.id }))
    );
  }, [contacts, contactsIDs, siblingSelectedIds]);

  const handleDeleteContact = (id: string) => {
    // deleteItem('contacts', id);
    if (config?.selectType === 'multiple') {
      setValue(`${pageName}.${name}`, contactsIDs?.filter((contactID: string) => contactID !== id));
      return;
    }
    setValue(`${pageName}.${name}`, undefined);
  };

  const onChangeContactSelect = (value: string) => {
    if (!value) return;

    switch (mode) {
      case 'documentationTab':
        setShowContactsDropdown(false);
        if (config?.selectType === 'multiple') {
          setValue(`${pageName}.${name}`, [...(contactsIDs || []), value]);
          return;
        }
        setValue(`${pageName}.${name}`, value);
        break;
      case 'wizard':
        openModalPage({ type: 'enter-contact-info', row, id: value });
        break;
      default:
        break;
    }
  };

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  return (
    <>
      <ThreeColumnWrapper isMobile={isMobile}>
        {!selectedContacts?.length && (
          <>
            {config?.selectType === 'multiple' && !showContactsDropdown ? (
              <AddSectionWrapper>
                {validation?.required && !ignoreRequired ? (
                  <HelperText>At least one contact is required to continue.</HelperText>
                ) : (
                  <AddSectionText>No contacts have been added yet.</AddSectionText>
                )}
                <Button
                  onClick={() => setShowContactsDropdown(true)}
                  styleType="outline"
                  icon={<AddButtonIcon fill={themeVar('font.action')} />}
                  size="md"
                  width="max-content"
                >
                  Add
                </Button>
              </AddSectionWrapper>
            ) : (
              <Select
                label={rowLabel ?? 'Contact'}
                setValue={onChangeContactSelect}
                options={contactSelectOptions}
                size="md"
                isRequiredField={!!validation?.required && !ignoreRequired}
                errorText={(errors?.[pageName] as FieldErrors)?.[name]?.message?.toString()}
                additionalOption={
                  <AddButtonWrapper>
                    <Button size="sm" styleType="outline" onClick={() => openModalPage({ type: 'enter-contact-info', row })}>
                      Add New Contact
                    </Button>
                  </AddButtonWrapper>
                }
              />
            )}
          </>
        )}
      </ThreeColumnWrapper>
      {!!selectedContacts.length && (
        <ContactsSectionContent>
          {selectedContacts?.map((contact) => (
            <div key={contact.id}>
              {contact?.error && <ErrorText size="sm">{contact.error}</ErrorText>}
              <ContactsWrapper isMobile={isMobile}>
                <ContactsItemsWrapper>
                  {contact.data.map(
                    (item) =>
                      item.value && (
                        <TwoColumnWrapper key={item.id} isMobile={isMobile}>
                          <ContactsItem>{item.label}</ContactsItem>
                          <ContactsItem isBold>{item.value}</ContactsItem>
                        </TwoColumnWrapper>
                      )
                  )}
                </ContactsItemsWrapper>
                <ButtonsWrapper id="editButton" isMobile={isMobile}>
                  <DeleteIconWrapper isMobile={isMobile} onClick={() => handleDeleteContact(contact.id)}>
                    <DeleteItemIcon width={23} height={23} fill={themeVar('context.error')} />
                  </DeleteIconWrapper>
                  <EditIconWrapper
                    isMobile={isMobile}
                    id="editButton"
                    onClick={() => openModalPage({ type: 'enter-contact-info', row, id: contact.id })}
                  >
                    <EditTodoIcon />
                  </EditIconWrapper>
                </ButtonsWrapper>
              </ContactsWrapper>
              {config?.selectType === 'multiple' && <Divider />}
            </div>
          ))}
          {!!(selectedContacts?.length && config?.selectType === 'multiple' && (!validation?.max || validation?.max > selectedContacts?.length)) && (
            <>
              {showContactsDropdown ? (
                <ThreeColumnWrapper isMobile={isMobile}>
                  <Select
                    label={rowLabel ?? 'Contact'}
                    setValue={onChangeContactSelect}
                    options={contactSelectOptions}
                    size="md"
                    additionalOption={
                      <AddButtonWrapper>
                        <Button size="sm" styleType="outline" onClick={() => openModalPage({ type: 'enter-contact-info', row })}>
                          Add New Contact
                        </Button>
                      </AddButtonWrapper>
                    }
                  />
                </ThreeColumnWrapper>
              ) : (
                <Button
                  onClick={() => setShowContactsDropdown(true)}
                  type="button"
                  styleType="outline"
                  icon={<AddButtonIcon fill={themeVar('font.action')} />}
                  size="md"
                  width="max-content"
                >
                  Add
                </Button>
              )}
            </>
          )}
          {config?.selectType === 'multiple' && validation?.max === selectedContacts?.length && (
            <AddSectionText>The maximum number of contacts has been added.</AddSectionText>
          )}
        </ContactsSectionContent>
      )}
    </>
  );
};

const AddSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 10px;
`;

const AddSectionText = styled.p`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;

const HelperText = styled.p`
  font-size: 13px;
  font-weight: 400;
  line-height: 18.2px;
  color: ${themeVar('context.error')};
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ContactsSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 20px 0;
  background-color: ${themeVar('border.base')};
`;

const ContactsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  padding: ${({ isMobile }) => (isMobile ? '0' : '20px')};
  transition: 0.1s ease-in-out;

  @media (hover: hover) {
    &:hover #editButton {
      opacity: 1;
    }

    &:hover {
      background-color: ${themeVar('layer.12')};
    }
  }
`;

const ContactsItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
`;

const ContactsItem = styled.div<{ isBold?: boolean }>`
  font-family: Blinker;
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 4px;
`;

const ButtonsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  gap: 10px;
  margin-left: ${({ isMobile }) => (isMobile ? '0' : 'auto')};
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
  transition: 0.1s ease-in-out;
`;

const EditIconWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : 'max-content')};
  padding: 12px;
  border-radius: 4px;
  background-color: ${themeVar('action.default')};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  cursor: pointer;
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
`;

const DeleteIconWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  height: 40px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 1px solid ${themeVar('context.error')};
`;
